import { Signal } from "signals";
import { ButtonEvent } from "slotworks/graphics/elements/button-element";
import FlexiButton from "slotworks/graphics/elements/flexi-button";
import FlexiText from "slotworks/graphics/elements/flexi-text";

export default class RadioGroup<T> {

    public onChange: Signal;
    public formatFunction: (value: number) => string;

    private buttons: FlexiButton[];
    private buttonListeners: Array<() => void>;
    private labels: FlexiText[];

    private rightButton: FlexiButton;
    private leftButton: FlexiButton;
    private rightPageButton: FlexiButton;
    private leftPageButton: FlexiButton;

    private values: T[];

    private viewPointer: number;
    private selectedIndex: number;

    constructor(buttons: FlexiButton[], labels: FlexiText[], rightButton?: FlexiButton, leftButton?: FlexiButton, rightPageButton?: FlexiButton, leftPageButton?: FlexiButton) {
        this.onChange = new Signal();

        this.buttons = buttons;
        this.labels = labels;

        this.rightButton = rightButton;
        this.leftButton = leftButton;
        this.rightPageButton = rightPageButton;
        this.leftPageButton = leftPageButton;

        if (this.rightButton) {
            this.rightButton.on(ButtonEvent.CLICK, this.prev);
        }

        if (this.leftButton) {
            this.leftButton.on(ButtonEvent.CLICK, this.next);
        }

        if (this.rightPageButton) {
            this.rightPageButton.on(ButtonEvent.CLICK, this.nextPage);
        }

        if (this.leftPageButton) {
            this.leftPageButton.on(ButtonEvent.CLICK, this.prevPage);
        }

        this.viewPointer = 0;

        this.buttonListeners = [];
        for (let viewPosition = 0; viewPosition < this.buttons.length; viewPosition++) {
            const button = this.buttons[viewPosition];
            const listener = () => {
                this.buttonPressed(viewPosition);
            };
            this.buttonListeners.push(listener);
            button.setEnabled(true);
            button.on(ButtonEvent.CLICK, listener);
        }
    }

    public updateValues(values: T[], defaultValue: T) {
        if (this.selectedIndex === values.indexOf(defaultValue)) {
            return;
        }

        this.values = values;

        this.selectedIndex = this.values.indexOf(defaultValue);

        if (this.values.length > this.buttons.length) {
            // If there are more values than buttons, move the view pointer to show the selected index in the center / on the current page
            if (this.leftButton && this.rightButton) {
                this.viewPointer = Math.min(this.buttons.length, Math.max(0, this.selectedIndex - Math.floor(this.buttons.length / 2)));
            }

            if (this.leftPageButton && this.rightPageButton) {
                this.viewPointer = Math.floor(this.selectedIndex / this.buttons.length) * this.buttons.length;
            }
        }

        this.hideUnusedPositionButtons();
        this.update();
    }

    public setEnabled(enabled: boolean) {
        for (const button of this.buttons) {
            button.setEnabled(enabled);
        }
    }

    public setVisible(visible: boolean) {
        this.buttons.forEach((button: FlexiButton) => {
            button.setVisible(visible);
        });

        this.labels.forEach((label: FlexiText) => {
            label.setVisible(visible);
        });

        if (this.rightButton) {
            this.rightButton.setVisible(visible);
        }

        if (this.leftButton) {
            this.leftButton.setVisible(visible);
        }

        if (this.rightPageButton) {
            this.rightPageButton.setVisible(visible);
        }

        if (this.leftPageButton) {
            this.leftPageButton.setVisible(visible);
        }
    }

    public destroy() {
        for (let viewPosition = 0; viewPosition < this.buttons.length; viewPosition++) {
            this.buttons[viewPosition].off(ButtonEvent.CLICK, this.buttonListeners[viewPosition]);
        }

        if (this.rightButton) {
            this.rightButton.off(ButtonEvent.CLICK, this.prev);
        }

        if (this.leftButton) {
            this.leftButton.off(ButtonEvent.CLICK, this.next);
        }

        this.onChange.removeAll();
    }

    private next = () => {
        this.viewPointer--;

        this.update();
    }

    private prev = () => {
        this.viewPointer++;

        this.update();
    }

    private prevPage = () => {
        this.viewPointer -= this.buttons.length;

        this.update();
    }

    private nextPage = () => {
        this.viewPointer += this.buttons.length;

        this.update();
    }

    private update() {
        for (let viewPosition = 0; viewPosition < this.labels.length; viewPosition++) {
            const listPosition = viewPosition + this.viewPointer;

            this.buttons[viewPosition].setSelected(false);
            if (listPosition === this.selectedIndex) {
                this.buttons[viewPosition].setSelected(true);
            }

            if (this.values[listPosition]) {
                this.buttons[viewPosition].setVisible(true);
                this.labels[viewPosition].setVisible(true);
                const label = this.labels[viewPosition];
                const value = this.values[listPosition];
                if (this.formatFunction) {
                    label.setText(this.formatFunction(value as any));
                } else {
                    label.setText(value.toString());
                }
            } else {
                this.buttons[viewPosition].setVisible(false);
                this.labels[viewPosition].setVisible(false);
            }
        }

        this.updateViewPositionButtons();
    }

    private updateViewPositionButtons() {
        if (this.leftPageButton) {
            this.leftPageButton.setEnabled(true);
            if (this.viewPointer - this.buttons.length < 0) {
                this.leftPageButton.setEnabled(false);
            }
        }

        if (this.rightPageButton) {
            this.rightPageButton.setEnabled(true);
            if (this.viewPointer + this.buttons.length >= this.values.length) {
                this.rightPageButton.setEnabled(false);
            }
        }

        if (this.leftButton) {
            this.leftButton.setEnabled(true);
            if (this.viewPointer - 1 < 0) {
                this.leftButton.setEnabled(false);
            }
        }

        if (this.rightButton) {
            this.rightButton.setEnabled(true);
            if (this.viewPointer + this.buttons.length >= this.values.length) {
                this.rightButton.setEnabled(false);
            }
        }
    }

    private hideUnusedPositionButtons() {
        if (this.buttons.length >= this.values.length) {
            if (this.leftPageButton) {
                this.leftPageButton.setVisible(false);
            }
            if (this.rightPageButton) {
                this.rightPageButton.setVisible(false);
            }
            if (this.leftButton) {
                this.leftButton.setVisible(false);
            }
            if (this.rightButton) {
                this.rightButton.setVisible(false);
            }
        }
    }

    private buttonPressed(viewPosition: number) {
        this.selectedIndex = this.viewPointer + viewPosition;

        this.onChange.dispatch(this.values[this.selectedIndex]);

        this.update();
    }
}
